import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import type { RecurringTemplate, RecurringTemplateInput } from '@/types/recurring';

const RECURRING_COLLECTION = 'recurringTemplates';

// Helper to remove undefined values from objects
const removeUndefined = <T extends Record<string, unknown>>(obj: T): Partial<T> => {
  const cleaned: Partial<T> = {};
  (Object.keys(obj) as Array<keyof T>).forEach((key) => {
    if (obj[key] !== undefined) {
      cleaned[key] = obj[key];
    }
  });
  return cleaned;
};

// Helper to convert Firestore timestamp to Date
const timestampToDate = (timestamp: Timestamp | { toDate: () => Date } | Date | string | number): Date => {
  // If it's already a Date, return it
  if (timestamp instanceof Date) {
    return timestamp;
  }
  // If it has a toDate method (Firestore Timestamp), call it
  if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
    return timestamp.toDate();
  }
  // Otherwise, try to convert it (string or number)
  return new Date(timestamp as string | number);
};

// Create a new recurring template
export async function createRecurringTemplate(
  userId: string,
  template: RecurringTemplateInput
): Promise<string> {
  const docRef = doc(collection(db, RECURRING_COLLECTION));
  
  // Build base template
  const recurringTemplate: Record<string, unknown> = {
    userId,
    name: template.name,
    amount: template.amount,
    type: template.type,
    categoryId: template.categoryId,
    frequency: template.frequency,
    startDate: Timestamp.fromDate(template.startDate),
    endDate: template.endDate ? Timestamp.fromDate(template.endDate) : null,
    isActive: true,
    createdAt: Timestamp.fromDate(new Date()),
    updatedAt: Timestamp.fromDate(new Date()),
  };

  // Only add frequency-specific fields if they exist
  if (template.dayOfWeek !== undefined) {
    recurringTemplate.dayOfWeek = template.dayOfWeek;
  }
  if (template.dayOfMonth !== undefined) {
    recurringTemplate.dayOfMonth = template.dayOfMonth;
  }
  if (template.monthOfYear !== undefined) {
    recurringTemplate.monthOfYear = template.monthOfYear;
  }

  // Remove any remaining undefined values
  const cleanedTemplate = removeUndefined(recurringTemplate);

  console.log('💾 Saving recurring template:', cleanedTemplate);

  await setDoc(docRef, cleanedTemplate);

  console.log('✅ Recurring template saved with ID:', docRef.id);
  return docRef.id;
}

// Get all recurring templates for a user
export async function getRecurringTemplates(
  userId: string
): Promise<RecurringTemplate[]> {
  const q = query(
    collection(db, RECURRING_COLLECTION),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      userId: data.userId,
      name: data.name,
      amount: data.amount,
      type: data.type,
      categoryId: data.categoryId,
      frequency: data.frequency,
      startDate: timestampToDate(data.startDate),
      endDate: data.endDate ? timestampToDate(data.endDate) : undefined,
      dayOfWeek: data.dayOfWeek,
      dayOfMonth: data.dayOfMonth,
      monthOfYear: data.monthOfYear,
      isActive: data.isActive,
      lastProcessedDate: data.lastProcessedDate
        ? timestampToDate(data.lastProcessedDate)
        : undefined,
      createdAt: timestampToDate(data.createdAt),
      updatedAt: timestampToDate(data.updatedAt),
    };
  });
}

// Get a single recurring template
export async function getRecurringTemplate(
  templateId: string
): Promise<RecurringTemplate | null> {
  const docRef = doc(db, RECURRING_COLLECTION, templateId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  const data = docSnap.data();
  return {
    id: docSnap.id,
    userId: data.userId,
    name: data.name,
    amount: data.amount,
    type: data.type,
    categoryId: data.categoryId,
    frequency: data.frequency,
    startDate: timestampToDate(data.startDate),
    endDate: data.endDate ? timestampToDate(data.endDate) : undefined,
    dayOfWeek: data.dayOfWeek,
    dayOfMonth: data.dayOfMonth,
    monthOfYear: data.monthOfYear,
    isActive: data.isActive,
    lastProcessedDate: data.lastProcessedDate
      ? timestampToDate(data.lastProcessedDate)
      : undefined,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt),
  };
}

// Update a recurring template
export async function updateRecurringTemplate(
  templateId: string,
  updates: Partial<RecurringTemplateInput> & { isActive?: boolean }
): Promise<void> {
  const docRef = doc(db, RECURRING_COLLECTION, templateId);

  const updateData: Record<string, unknown> = {
    ...updates,
    updatedAt: Timestamp.fromDate(new Date()),
  };

  if (updates.startDate) {
    updateData.startDate = Timestamp.fromDate(updates.startDate);
  }

  if (updates.endDate) {
    updateData.endDate = Timestamp.fromDate(updates.endDate);
  } else if (updates.endDate === null) {
    updateData.endDate = null;
  }

  // Remove undefined values before updating
  const cleanedUpdate = removeUndefined(updateData);

  await updateDoc(docRef, cleanedUpdate);
}

// Delete a recurring template
export async function deleteRecurringTemplate(templateId: string): Promise<void> {
  const docRef = doc(db, RECURRING_COLLECTION, templateId);
  await deleteDoc(docRef);
}

// Toggle active status
export async function toggleRecurringTemplate(
  templateId: string,
  isActive: boolean
): Promise<void> {
  await updateRecurringTemplate(templateId, { isActive });
}

// Update last processed date
export async function updateLastProcessedDate(
  templateId: string,
  date: Date
): Promise<void> {
  const docRef = doc(db, RECURRING_COLLECTION, templateId);
  await updateDoc(docRef, {
    lastProcessedDate: Timestamp.fromDate(date),
    updatedAt: Timestamp.fromDate(new Date()),
  });
}

// Get active recurring templates that need processing
export async function getActiveRecurringTemplates(
  userId: string
): Promise<RecurringTemplate[]> {
  const q = query(
    collection(db, RECURRING_COLLECTION),
    where('userId', '==', userId),
    where('isActive', '==', true)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      userId: data.userId,
      name: data.name,
      amount: data.amount,
      type: data.type,
      categoryId: data.categoryId,
      frequency: data.frequency,
      startDate: timestampToDate(data.startDate),
      endDate: data.endDate ? timestampToDate(data.endDate) : undefined,
      dayOfWeek: data.dayOfWeek,
      dayOfMonth: data.dayOfMonth,
      monthOfYear: data.monthOfYear,
      isActive: data.isActive,
      lastProcessedDate: data.lastProcessedDate
        ? timestampToDate(data.lastProcessedDate)
        : undefined,
      createdAt: timestampToDate(data.createdAt),
      updatedAt: timestampToDate(data.updatedAt),
    };
  });
}